import { ChevronLeft } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import config from '@/config';
import { usePrivyAccount } from '@/hooks';
import useOnramperUrl from '@/lib/api/get-onramper-url';
import { useAccountStore } from '@/store/use-account-store';

const ONRAMPER_API_KEY = import.meta.env.VITE_ONRAMPER_API_KEY;

if (!ONRAMPER_API_KEY)
  throw new Error('Environment variable missing: VITE_ONRAMPER_API_KEY');

const themeParams = new URLSearchParams();
themeParams.set('primaryColor', '163326');
themeParams.set('primaryTextColor', '0A0A0A');
themeParams.set('secondaryTextColor', '696B6B');
themeParams.set('primaryBtnTextColor', 'ffffff');

const { isDevelopment } = config;
const subdomain = isDevelopment ? 'paper' : 'trade';
const redirectUrl = encodeURIComponent(
  `https://${subdomain}.vestmarkets.com/portfolio`,
);

export default function OnramperPage() {
  const { email } = usePrivyAccount();
  const { data: url, isLoading } = useOnramperUrl();

  const address = useAccountStore((state) => state.address);

  const partnerContext = useMemo(() => {
    const time = useAccountStore.getState().getAccurateTime();
    return encodeURIComponent(`${address}:${time}`);
  }, [address]);

  return (
    <section className="px-6 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <Link to="/deposit" className="flex items-center gap-2">
          <ChevronLeft size={16} />
          Back to Deposit
        </Link>
        <div className="my-8 flex flex-col pb-4">
          <h1 className="mb-4 text-4xl font-bold">Onramper</h1>
          <p className="text-vestgrey-100">Fund your account using Onramper.</p>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center bg-background py-8">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary"></div>
                <div className="absolute inset-0 h-8 w-8 animate-ping rounded-full border border-primary opacity-20"></div>
              </div>
              <p className="font-mono text-sm text-vestgrey-100">Loading...</p>
            </div>
          </div>
        ) : (
          url && (
            <iframe
              className="w-full border border-border"
              src={`${url}&email=${email}&${themeParams.toString()}&successRedirectUrl=${redirectUrl}&failureRedirectUrl=${redirectUrl}&partnerContext=${partnerContext}&hideTopBar=true`}
              title="Onramper Widget"
              height="580px"
              allow="accelerometer; autoplay; camera; gyroscope; payment; microphone"
            />
          )
        )}
      </div>
    </section>
  );
}
